/* eslint-disable no-undef */

import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectApp } from "../slices/AppsSlice";
import { logout } from "../slices/TokensSlice";
import { deleteAccessToken } from "../AuthService";
import { INDIGO, SLATE } from "../constants/Colors";
import { NavigationIcons } from "./utils/InsyncIconsUtil";
import {
  MENU_ITEMS_ADMIN,
  MENU_ITEMS_APP,
  MENU_ITEMS_AGENT,
} from "../constants/Constants";
import NavbarLoadingPlaceholder from "./LoadingPlaceholders/NavbarLoadingPlaceholder";
import { getRedirectUrl } from "../utils";

// ─── Layout constants ─────────────────────────────────────────────────────────
const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 208;
const SIDEBAR_COLLAPSED_KEY = "ui.sidebar.collapsed";
const LEGACY_AGENT_HIDDEN_ITEMS = new Set(["Prompt Variables", "System Prompt"]);


// ─── MUI icon resolver ────────────────────────────────────────────────────────
const MUI_ICON_MAP = {
  DashboardOutlined:   DashboardOutlinedIcon,
  DescriptionOutlined: DescriptionOutlinedIcon,
  StorageOutlined:     StorageOutlinedIcon,
  PeopleOutlined:      PeopleOutlinedIcon,
  CreditCardOutlined:  CreditCardOutlinedIcon,
  DnsOutlined:         DnsOutlinedIcon,
  SmartToyOutlined:    SmartToyOutlinedIcon,
  Inventory2Outlined:  Inventory2OutlinedIcon,
  BarChartOutlined:    BarChartOutlinedIcon,
  SettingsOutlined:    SettingsOutlinedIcon,
  MenuBookOutlined:    MenuBookOutlinedIcon,
  BoltOutlined:        BoltOutlinedIcon,
  TimelineOutlined:    TimelineOutlinedIcon,
  TerminalOutlined:    TerminalOutlinedIcon,
  AdminPanelSettingsOutlined: AdminPanelSettingsOutlinedIcon,
  ArticleOutlined:            ArticleOutlinedIcon,
  TuneOutlined:               TuneOutlinedIcon,
  LanguageOutlined:           LanguageOutlinedIcon,
  FolderOutlined:             FolderOutlinedIcon,
  SearchOutlined:             SearchOutlinedIcon,
  AccountTreeOutlined:        AccountTreeOutlinedIcon,
  PersonOutlined:             PersonOutlinedIcon,
  PsychologyOutlined:         PsychologyOutlinedIcon,
  BusinessOutlined:           BusinessOutlinedIcon,
  WarningAmberOutlined:       WarningAmberOutlinedIcon,
  BlockOutlined:              BlockOutlinedIcon,
  SchoolOutlined:             SchoolOutlinedIcon,
  ManageAccountsOutlined:     ManageAccountsOutlinedIcon,
  InsightsOutlined:           InsightsOutlinedIcon,
  PaletteOutlined:            PaletteOutlinedIcon,
  ScienceOutlined:            ScienceOutlinedIcon,
  LockOutlined:               LockOutlinedIcon,
  BugReportOutlined:          BugReportOutlinedIcon,
};

/**
 * Renders the right icon based on whether the menu item uses a MUI icon
 * (muiIcon string key) or a legacy NavigationIcons string (icon key).
 */
function ItemIcon({ muiIcon, icon, active }) {
  if (muiIcon && MUI_ICON_MAP[muiIcon]) {
    const MuiComponent = MUI_ICON_MAP[muiIcon];
    return (
      <MuiComponent
        sx={{ fontSize: 18, color: active ? INDIGO[600] : SLATE[400] }}
      />
    );
  }
  if (icon) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          color: active ? INDIGO[600] : SLATE[400],
          "& svg": { width: 18, height: 18 },
        }}
      >
        <NavigationIcons icon={icon} />
      </Box>
    );
  }
  return null;
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tokens = useSelector((state) => state?.tokens.value);
  const user = useSelector((state) => state?.user.value);
  const [open, setOpen] = useState({});
  const apps = useSelector((state) => state?.apps.list || []);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentMode = useSelector((state) => state?.apps?.selectedAgentMode);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
  );
  const [isApp, setIsApp] = useState(false);
  const [isAgentView, setIsAgentView] = useState(false);
  const appsLoaded = useSelector((state) => state?.apps?.appsLoaded);
  const [loading, setLoading] = useState(() => apps.length === 0);
  const [agentHasKB, setAgentHasKB] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [clearStorageDialogOpen, setClearStorageDialogOpen] = useState(false);


  // ── Apps are fetched by useEnsureAppsLoaded in NavigationRoutes ──────────
  useEffect(() => {
    if (appsLoaded) setLoading(false);
  }, [appsLoaded]);

  const currentPathSegments = location.pathname.split("/");
  const currentAppNameFromUrl = currentPathSegments[1];

  const currentAgentId = useMemo(() => {
    const match = location.pathname.match(/\/agent\/(\d+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const resolvePath = useCallback(
    (path) => {
      if (!path) return "";
      if (path === "/admin" || path.startsWith("/admin/")) return path; // global admin paths — never prefixed
      if (isApp) {
        if (isAgentView && currentAgentId) {
          return `/${currentAppNameFromUrl}/agent/${currentAgentId}${path}`;
        }
        return `/${currentAppNameFromUrl}${path}`;
      }
      return path;
    },
    [currentAppNameFromUrl, isAgentView, isApp, currentAgentId]
  );

  useEffect(() => {
    let selectedApp = apps.find(
      (app) => app.url_code === currentAppNameFromUrl
    );
    if (currentAppNameFromUrl && selectedApp) {
      if (!currentApp) dispatch(selectApp(selectedApp));
      setIsApp(true);
      const agentPattern = new RegExp(
        `^/${currentAppNameFromUrl}/agent/\\d+`
      );
      setIsAgentView(agentPattern.test(location.pathname));
    } else {
      setIsApp(false);
      setIsAgentView(false);
    }
  }, [apps, currentAppNameFromUrl, location.pathname, currentApp, dispatch]);

  useEffect(() => {
    if (!isAgentView) {
      setAgentHasKB(true);
    }
  }, [isAgentView]);

  // Reset logo error state when app changes
  useEffect(() => {
    setLogoError(false);
  }, [currentApp?.id]);

  // ── Menu items ───────────────────────────────────────────────────────────
  const menuItems = useMemo(() => {
    if (!isApp) return MENU_ITEMS_ADMIN;
    if (isAgentView) {
      return MENU_ITEMS_AGENT.filter((item) => {
        if (item.conditional && item.title === "Knowledge Bases") {
          return agentHasKB;
        }
        if (selectedAgentMode === 'legacy' && LEGACY_AGENT_HIDDEN_ITEMS.has(item.title)) {
          return false;
        }
        return true;
      });
    }
    const mode = currentApp?.client_mode;
    const showAgentBuilder = !mode || mode === 'agent' || mode === 'hybrid';
    return MENU_ITEMS_APP.filter(item => item.title !== 'Agents' || showAgentBuilder);
  }, [isApp, isAgentView, agentHasKB, selectedAgentMode, currentApp?.client_mode]);

  // Auto-open parent groups whose children match current path
  useEffect(() => {
    const toOpen = {};
    menuItems.forEach((item) => {
      if (!item.children) return;
      item.children.forEach((subItem) => {
        const subPath = resolvePath(subItem.path);
        if (
          subPath &&
          (location.pathname === subPath ||
            location.pathname.startsWith(subPath + "/"))
        ) {
          toOpen[item.title] = true;
        }
        if (subItem.children) {
          subItem.children.forEach((nested) => {
            const nestedPath = resolvePath(nested.path);
            if (
              nestedPath &&
              (location.pathname === nestedPath ||
                location.pathname.startsWith(nestedPath + "/"))
            ) {
              toOpen[item.title] = true;
              toOpen[subItem.title] = true;
            }
          });
        }
      });
    });
    if (Object.keys(toOpen).length > 0) {
      setOpen((prev) => ({ ...prev, ...toOpen }));
    }
  }, [location.pathname, menuItems, resolvePath]);

  const handleItemClick = (title, path, hasChildren = false) => {
    if (hasChildren) {
      setOpen((prev) => ({ ...prev, [title]: !prev[title] }));
      return;
    }
    const resolvedPath = resolvePath(path);
    if (!resolvedPath) return;
    if (location.pathname === resolvedPath) {
      window.location.reload();
    } else {
      navigate(resolvedPath);
    }
  };

  const isSelected = (path) => {
    const resolvedPath = resolvePath(path);
    if (!resolvedPath) return false;
    return (
      location.pathname === resolvedPath ||
      location.pathname.startsWith(resolvedPath + "/")
    );
  };

  // Keep --sidebar-width CSS variable in sync so TopBar + PageWindow
  // can animate their left/margin offsets in tandem with the sidebar.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      `${collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px`
    );
  }, [collapsed]);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  // ── User initials for avatar ─────────────────────────────────────────────
  const userInitials = useMemo(() => {
    const name = user?.name || user?.email || "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [user]);

  // Shared item button sx — matches demo's px-3 py-2 rounded-lg style
  const itemSx = (isActive, depth) => ({
    borderRadius: "8px",
    px: collapsed ? "10px" : depth > 0 ? "12px" : "12px",
    py: "8px",
    mx: depth > 0 ? "4px" : 0,
    minHeight: "36px",
    justifyContent: collapsed ? "center" : "flex-start",
    gap: "8px",
    mb: "2px",
    cursor: "pointer",
    bgcolor: isActive ? INDIGO[50] : "transparent",
    color: isActive ? INDIGO[700] : SLATE[600],
    fontWeight: isActive ? 500 : 400,
    "&:hover": {
      bgcolor: isActive ? INDIGO[50] : SLATE[50],
      color: isActive ? INDIGO[700] : SLATE[900],
    },
    "&.Mui-selected": {
      bgcolor: INDIGO[50],
      color: INDIGO[700],
      "&:hover": { bgcolor: INDIGO[50] },
    },
  });

  const renderMenuItems = (items, depth = 0) =>
    items.map((item, index) => {
      const hasChildren = !!item.children;
      const isOpen = !!open[item.title];
      const isItemSelected =
        isSelected(item.alternatePath) || isSelected(item.path);

      if (hasChildren) {
        const anyChildActive = item.children.some(
          (sub) =>
            isSelected(sub.path) ||
            (sub.children && sub.children.some((n) => isSelected(n.path)))
        );

        const trigger = (
          <ListItemButton
            onClick={() => handleItemClick(item.title, item.path, true)}
            selected={anyChildActive}
            sx={{
              ...itemSx(anyChildActive, depth),
              bgcolor: "transparent",
              "&.Mui-selected": { bgcolor: "transparent" },
              "&.Mui-selected:hover": { bgcolor: SLATE[50] },
            }}
          >
            {(item.muiIcon || item.icon) && (
              <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                <ItemIcon muiIcon={item.muiIcon} icon={item.icon} active={anyChildActive} />
              </Box>
            )}
            {!collapsed && (
              <>
                <ListItemText
                  primary={item.title}
                  sx={{
                    m: 0,
                    "& .MuiTypography-root": {
                      fontSize: "0.8125rem",
                      fontWeight: anyChildActive ? 500 : 400,
                      color: "inherit",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                {isOpen ? (
                  <ExpandLessIcon sx={{ fontSize: 16, color: SLATE[400], flexShrink: 0 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16, color: SLATE[400], flexShrink: 0 }} />
                )}
              </>
            )}
          </ListItemButton>
        );

        // When collapsed, skip the group header and render children directly as icon-only items
        if (collapsed) {
          return (
            <React.Fragment key={index}>
              {renderMenuItems(item.children, depth)}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            {trigger}
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: 1 }}>
                {renderMenuItems(item.children, depth + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }

      // Leaf item with optional divider above it
      const handleLeafClick = () => {
        if (item.redirect && item.path) {
          window.open(getRedirectUrl(currentApp, item.path), "_blank");
        } else if (item.path) {
          handleItemClick(item.title, item.path);
        }
      };

      const leafButton = (
        <ListItemButton
          onClick={handleLeafClick}
          selected={isItemSelected}
          sx={itemSx(isItemSelected, depth)}
        >
          {(item.muiIcon || item.icon) && (
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
              <ItemIcon muiIcon={item.muiIcon} icon={item.icon} active={isItemSelected} />
            </Box>
          )}
          {!collapsed && (
            <ListItemText
              primary={item.title}
              sx={{
                m: 0,
                "& .MuiTypography-root": {
                  fontSize: "0.8125rem",
                  fontWeight: isItemSelected ? 500 : 400,
                  color: "inherit",
                  whiteSpace: "nowrap",
                },
              }}
            />
          )}
        </ListItemButton>
      );

      return (
        <React.Fragment key={index}>
          {item.divider && (
            <Divider sx={{ borderColor: SLATE[200], my: 1 }} />
          )}
          {collapsed ? (
            <Tooltip title={item.title} placement="right" arrow>
              {leafButton}
            </Tooltip>
          ) : (
            leafButton
          )}
        </React.Fragment>
      );
    });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flexShrink: 0,
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1200,
        bgcolor: "#ffffff",
        borderRight: `1px solid ${SLATE[200]}`,
        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease",
      }}
    >
      {/* ── Brand header ────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: collapsed ? 0 : 2,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.07)",
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          minHeight: 64,
          flexShrink: 0,
          cursor: "pointer",
          overflow: "hidden",
        }}
        onClick={() => {
          dispatch(selectApp(null));
          window.location.href = "/";
        }}
      >
        <img
          src="https://cdn.prod.website-files.com/6274198a408986047282f9bd/67f3cd13c67b44b4a35620ce_InsyncLogoHead.png"
          alt="InSyncAI"
          style={{
            width: collapsed ? 32 : 112,
            height: 32,
            objectFit: "contain",
            flexShrink: 0,
            transition: "width 0.2s ease",
          }}
        />
        {!collapsed && (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#1d1d1f",
              whiteSpace: "nowrap",
              lineHeight: 1,
            }}
          >
            AI Studio
          </Typography>
        )}
      </Box>

      {/* ── Nav items ────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: collapsed ? 1 : 2 }}>
        {loading ? (
          <NavbarLoadingPlaceholder itemCount={9} collapsed={collapsed} />
        ) : (
          <>
            {/* Client logo icon — shown when collapsed and inside an app */}
            {isApp && currentApp && collapsed && (
              <Tooltip title={currentApp.client_name} placement="right" arrow>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: SLATE[50],
                    border: `1px solid ${SLATE[200]}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    mx: "auto",
                    overflow: "hidden",
                  }}
                >
                  {currentApp.logo_url && !logoError ? (
                    <img
                      src={encodeURI(currentApp.logo_url)}
                      alt={currentApp.client_name}
                      style={{ width: 28, height: 28, objectFit: "contain" }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: INDIGO[600] }}>
                      {(currentApp.client_name || "?")[0].toUpperCase()}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )}

            {/* Client info section — shown when inside an app */}
            {isApp && currentApp && !collapsed && (
              <Box sx={{ mb: 1 }}>
                {/* Client card */}
                <Box
                  onClick={() => navigate(`/${currentApp.url_code}/`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    px: "10px",
                    py: "10px",
                    borderRadius: "10px",
                    bgcolor: SLATE[50],
                    border: `1px solid ${SLATE[200]}`,
                    mb: 1.5,
                    cursor: "pointer",
                    "&:hover": { bgcolor: SLATE[100], borderColor: SLATE[300] },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      bgcolor: "#ffffff",
                      border: `1px solid ${SLATE[200]}`,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {currentApp.logo_url && !logoError ? (
                      <img
                        src={encodeURI(currentApp.logo_url)}
                        alt={currentApp.client_name}
                        style={{ width: 32, height: 32, objectFit: "contain" }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: INDIGO[600] }}>
                        {(currentApp.client_name || "?")[0].toUpperCase()}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ overflow: "hidden" }}>
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: SLATE[900],
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {currentApp.client_name}
                    </Typography>
                    {currentApp.client_type && (
                      <Typography
                        sx={{
                          fontSize: "0.6875rem",
                          color: SLATE[500],
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {currentApp.client_type === "higher_ed"
                          ? "Higher Education"
                          : currentApp.client_type.charAt(0).toUpperCase() +
                            currentApp.client_type.slice(1)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: SLATE[200], mb: 1.5 }} />
              </Box>
            )}

            <List disablePadding>
              {renderMenuItems(menuItems)}
            </List>

            {/* Navigation (back link) — shown inside nav area after menu items */}
            {isApp && !collapsed && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ borderColor: SLATE[200], mb: 1.5 }} />
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 700,
                    color: SLATE[400],
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mb: 0.75,
                    px: "12px",
                  }}
                >
                  Navigation
                </Typography>
                <Tooltip
                  title={isAgentView ? `Back to ${currentApp?.client_name || "Client"}` : "Back to Clients"}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={() => navigate(isAgentView ? `/${currentAppNameFromUrl}/` : "/clients")}
                    sx={{
                      borderRadius: "8px",
                      px: "12px",
                      py: "8px",
                      minHeight: "36px",
                      gap: "6px",
                      color: SLATE[600],
                      "&:hover": { bgcolor: SLATE[50], color: SLATE[900] },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 16, color: SLATE[400], flexShrink: 0 }} />
                    <ListItemText
                      primary={isAgentView ? `Back to ${currentApp?.client_name || "Client"}` : "Back to Clients"}
                      sx={{
                        m: 0,
                        overflow: "hidden",
                        "& .MuiTypography-root": {
                          fontSize: "0.8125rem",
                          fontWeight: 400,
                          color: "inherit",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </Box>
            )}

            {/* Collapsed: icon-only back button */}
            {isApp && collapsed && (
              <Box sx={{ mt: 1 }}>
                <Tooltip
                  title={isAgentView ? `Back to ${currentApp?.client_name || "Client"}` : "Back to Clients"}
                  placement="right"
                  arrow
                >
                  <IconButton
                    onClick={() => navigate(isAgentView ? `/${currentAppNameFromUrl}/` : "/clients")}
                    size="small"
                    sx={{
                      width: 36,
                      height: 36,
                      color: SLATE[500],
                      mx: "auto",
                      display: "flex",
                      borderRadius: "8px",
                      "&:hover": { bgcolor: SLATE[100], color: SLATE[700] },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Box>

      <Divider sx={{ borderColor: SLATE[200] }} />

      {/* ── Clear Local Storage ──────────────────────────────────────────── */}
      <Box sx={{ px: collapsed ? 0 : 2, pt: 1.5, display: "flex", justifyContent: "center" }}>
        {collapsed ? (
          <Tooltip title="Clear Local Storage" placement="right" arrow>
            <IconButton
              onClick={() => setClearStorageDialogOpen(true)}
              size="small"
              sx={{
                color: SLATE[500],
                borderRadius: "8px",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: SLATE[100], color: SLATE[700] },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box
            onClick={() => setClearStorageDialogOpen(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              width: "100%",
              px: 2,
              py: "8px",
              borderRadius: "8px",
              border: `1px solid ${SLATE[200]}`,
              cursor: "pointer",
              color: SLATE[600],
              fontSize: "0.8125rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: SLATE[50], borderColor: SLATE[300] },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            Clear Local Storage
          </Box>
        )}
      </Box>

      <Dialog open={clearStorageDialogOpen} onClose={() => setClearStorageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Clear Local Storage</Typography>
          <IconButton size="small" onClick={() => setClearStorageDialogOpen(false)} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 3 }}>
            This will clear all locally stored data (including your session) and reload the page. Are you sure?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={() => setClearStorageDialogOpen(false)}
              variant="outlined"
              size="small"
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => { localStorage.clear(); window.location.href = "/"; }}
              variant="contained"
              size="small"
              sx={{ bgcolor: INDIGO[600], textTransform: "none", borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: INDIGO[700], boxShadow: "none" } }}
            >
              Clear
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── User profile ─────────────────────────────────────────────────── */}
      {user && (
        <>
          <Box
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            sx={{
              px: collapsed ? 0 : 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 1.5,
              minHeight: 56,
              cursor: "pointer",
              "&:hover": { bgcolor: SLATE[50] },
            }}
          >
            <Tooltip
              title={collapsed ? (user.name || user.email || "") : ""}
              placement="right"
              arrow
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  bgcolor: INDIGO[500],
                  color: "#ffffff",
                  flexShrink: 0,
                }}
              >
                {userInitials}
              </Avatar>
            </Tooltip>
            {!collapsed && (
              <Box sx={{ overflow: "hidden", flex: 1 }}>
                {user.name && (
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      color: SLATE[900],
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user.name}
                  </Typography>
                )}
                {user.email && (
                  <Typography
                    sx={{
                      fontSize: "0.6875rem",
                      color: SLATE[500],
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user.email}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* User dropdown menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { minWidth: 200 } } }}
          >
            <MenuItem
              onClick={() => {
                const apiHost = window.location.hostname.replace(/^app/, 'api');
                window.open(`${window.location.protocol}//${apiHost}/sidekiq`, '_blank');
                setUserMenuAnchor(null);
              }}
              sx={{ fontSize: "0.875rem", color: SLATE[700], gap: 1.5 }}
            >
              <StorageOutlinedIcon sx={{ fontSize: '1rem', color: SLATE[500] }} />
              Sidekiq
            </MenuItem>
            <MenuItem
              onClick={() => {
                setLogoutDialogOpen(true);
                setUserMenuAnchor(null);
              }}
              sx={{ fontSize: "0.875rem", color: SLATE[700], gap: 1.5 }}
            >
              <LockOutlinedIcon sx={{ fontSize: '1rem', color: SLATE[500] }} />
              Logout
            </MenuItem>
          </Menu>

          <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Logout</Typography>
              <IconButton size="small" onClick={() => setLogoutDialogOpen(false)} sx={{ color: SLATE[400] }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 3 }}>
                Are you sure you want to logout?
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={() => setLogoutDialogOpen(false)}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", borderRadius: "8px", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    dispatch(logout());
                    deleteAccessToken();
                    localStorage.removeItem("user");
                    localStorage.removeItem("tokens");
                    window.location.href = "/";
                  }}
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: INDIGO[600], textTransform: "none", borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: INDIGO[700], boxShadow: "none" } }}
                >
                  Logout
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* ── Collapse toggle ──────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-end",
          pb: 1.5,
        }}
      >
        <Tooltip
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          placement="right"
          arrow
        >
          <IconButton
            onClick={toggleCollapse}
            size="small"
            sx={{
              width: 24,
              height: 24,
              bgcolor: "#ffffff",
              border: `1px solid ${SLATE[200]}`,
              borderRadius: "50%",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              color: SLATE[500],
              position: "absolute",
              right: -12,
              bottom: 12,
              zIndex: 10,
              "&:hover": {
                bgcolor: INDIGO[50],
                color: INDIGO[600],
                borderColor: INDIGO[200],
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 14 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
